import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle, CheckCircle2, ChevronRight, MessageCircle,
  TrendingUp, TrendingDown, Wallet, Star, ClipboardX, Inbox,
} from 'lucide-react';
import axios from 'axios';
import { OutletFilter } from '../components/OutletFilter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (n == null ? '0' : Math.round(Number(n)).toLocaleString('en-IN'));
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

const QUICK_RANGES = [
  { key: 'today', label: 'Today', getValue: () => { const t = new Date().toISOString().slice(0,10); return { start: t, end: t }; } },
  { key: 'last7', label: 'Last 7 days', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(e.getDate()-6); return { start: s.toISOString().slice(0,10), end: e.toISOString().slice(0,10) }; } },
  { key: 'mtd', label: 'This Month', getValue: () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: n.toISOString().slice(0,10) }; } },
  { key: 'lm', label: 'Last Month', getValue: () => { const n = new Date(); const lm = new Date(n.getFullYear(), n.getMonth()-1, 1); const le = new Date(n.getFullYear(), n.getMonth(), 0); return { start: lm.toISOString().slice(0,10), end: le.toISOString().slice(0,10) }; } },
  { key: 'qtd', label: 'This Quarter', getValue: () => { const n = new Date(); const q = Math.floor(n.getMonth()/3); const qs = new Date(n.getFullYear(), q*3, 1); return { start: qs.toISOString().slice(0,10), end: n.toISOString().slice(0,10) }; } },
  { key: 'ytd', label: 'YTD', getValue: () => { const n = new Date(); return { start: `${n.getFullYear()}-01-01`, end: n.toISOString().slice(0,10) }; } },
  { key: 'custom', label: 'Custom Range', getValue: () => null },
];

// ── Reusable section shell — premium card with consistent padding ──
function Section({ title, icon: Icon, action, children, testId, className = '' }) {
  return (
    <Card className={`border-[#E2E8DF] shadow-sm rounded-xl overflow-hidden mb-3 lg:mb-4 ${className}`} data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between px-3 sm:px-4 lg:px-5 pt-3 lg:pt-4 pb-2 lg:pb-3">
          <h3 className="text-[14px] font-semibold text-[#1A2421] flex items-center gap-2 tracking-tight">
            {Icon && <Icon className="w-4 h-4 text-[color:var(--color-primary)]" strokeWidth={2} />}
            {title}
          </h3>
          {action}
        </div>
      )}
      <CardContent className="p-0">
        {children}
      </CardContent>
    </Card>
  );
}

// ── Decision tile (KPI) ─────────────────────────────────────
function DecisionTile({ label, value, subtitle, icon: Icon, trend, testId }) {
  const trendBg = trend === 'up' ? 'bg-[#2D6A4F]/10' : trend === 'down' ? 'bg-[#C45A41]/10' : 'bg-[#F3F5F2]';
  const trendFg = trend === 'up' ? 'text-[#2D6A4F]' : trend === 'down' ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]';
  return (
    <Card className="border-[#E2E8DF] shadow-sm rounded-xl" data-testid={testId}>
      <CardContent className="p-3 lg:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium leading-tight">{label}</p>
            <p className="text-[20px] sm:text-[22px] lg:text-[24px] font-semibold text-[#1A2421] mt-0.5 leading-none tracking-tight tabular-nums">{value}</p>
            {subtitle && <p className="text-[11px] text-[#5C6B64] mt-1 truncate">{subtitle}</p>}
          </div>
          <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${trendBg}`}>
            <Icon className={`w-4 h-4 ${trendFg}`} strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiStrip({ data }) {
  if (!data) return null;
  const totals = data.sales_channel || {};
  const sentiment = data.sentiment || {};
  const failures = data.checklist_failures || [];
  const cost = data.cost_efficiency || [];
  const fcRow = cost.find(c => c.metric === 'Food Cost %');
  const bcRow = cost.find(c => c.metric === 'Beverage Cost %');
  const overBudgetCount = cost.filter(c => c.status === 'over').length;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 lg:gap-3 mb-3 lg:mb-4">
      <DecisionTile testId="insights-kpi-total-sales" icon={Wallet} label="Total Sales"
        value={`₹${fmt(totals.total_sales)}`}
        subtitle={`${totals.total_orders || 0} orders · AOV ${totals.total_aov != null ? `₹${fmt(totals.total_aov)}` : '—'}`} />
      <DecisionTile testId="insights-kpi-cost-pulse"
        icon={overBudgetCount > 0 ? TrendingUp : TrendingDown}
        trend={overBudgetCount > 0 ? 'down' : (fcRow?.actual != null ? 'up' : null)}
        label="Cost Pulse"
        value={`FC ${pct(fcRow?.actual)} · BC ${pct(bcRow?.actual)}`}
        subtitle={overBudgetCount > 0 ? `${overBudgetCount} over budget` : 'Within range'} />
      <DecisionTile testId="insights-kpi-sentiment" icon={Star}
        trend={sentiment.positive_pct >= 60 ? 'up' : sentiment.negative_pct >= 30 ? 'down' : null}
        label="Sentiment"
        value={`${sentiment.positive_pct || 0}% positive`}
        subtitle={`${sentiment.total || 0} responses · ${sentiment.negative_pct || 0}% negative`} />
      <DecisionTile testId="insights-kpi-failures" icon={ClipboardX}
        trend={failures.length > 0 ? 'down' : 'up'}
        label="Checklist Failures"
        value={`${failures.length}`}
        subtitle={failures.length > 0 ? 'Needs review' : 'All clear'} />
    </div>
  );
}

// ── 1) Sales & Channel ─────────────────────────────────────
function SalesChannelSection({ data }) {
  if (!data) return null;
  const { channels = [], total_sales = 0, total_orders = 0, total_aov = null, flags = [] } = data;
  return (
    <Section title="Sales & Channel" icon={Wallet} testId="insights-sales-channel-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-[#1A2421]">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] border-b border-[#E2E8DF]">
              <th className="px-3 sm:px-4 py-3 font-medium">Channel</th>
              <th className="px-3 py-3 font-medium text-right">Sales</th>
              <th className="px-3 py-3 font-medium text-right">Orders</th>
              <th className="px-3 py-3 font-medium text-right">AOV</th>
              <th className="px-3 sm:px-4 py-3 font-medium text-right">% Contribution</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.key} className="border-b border-[#E2E8DF] last:border-b-0 hover:bg-[#F9F9F8]/50 transition-colors"
                data-testid={`insights-channel-row-${c.key}`}>
                <td className="px-3 sm:px-4 py-2.5 lg:py-3 font-medium">{c.label}</td>
                <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums font-semibold">₹{fmt(c.sales)}</td>
                <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums text-[#5C6B64]">
                  {c.orders > 0 ? c.orders.toLocaleString('en-IN') : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums font-semibold">
                  {c.aov != null ? `₹${fmt(c.aov)}` : <span className="text-[#9CA3AF] font-normal">—</span>}
                </td>
                <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-right">
                  <div className="inline-flex items-center gap-2 justify-end w-full">
                    <div className="hidden sm:block flex-1 max-w-[100px] h-1.5 rounded-full bg-[#F3F5F2] overflow-hidden">
                      <div className="h-full bg-[color:var(--color-primary)]" style={{ width: `${c.share_pct || 0}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums w-12 text-right">{c.share_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[color:var(--color-primary)]/5" data-testid="insights-channel-total-row">
              <td className="px-3 sm:px-4 py-2.5 lg:py-3 font-semibold text-[color:var(--color-primary)]">Total</td>
              <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums font-bold text-[color:var(--color-primary)]">₹{fmt(total_sales)}</td>
              <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums text-[color:var(--color-primary)]">
                {total_orders > 0 ? total_orders.toLocaleString('en-IN') : <span className="text-[#9CA3AF]">—</span>}
              </td>
              <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums font-bold text-[color:var(--color-primary)]">
                {total_aov != null ? `₹${fmt(total_aov)}` : <span className="text-[#9CA3AF] font-normal">—</span>}
              </td>
              <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-right text-xs text-[#5C6B64] tabular-nums">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {flags.length > 0 && (
        <div className="px-3 sm:px-4 py-3 bg-[#C45A41]/5 border-t border-[#C45A41]/20 flex flex-col gap-1.5"
          data-testid="insights-sales-flags">
          {flags.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-[#1A2421]">
              <AlertTriangle className="w-3.5 h-3.5 text-[#C45A41] shrink-0" />
              <span>{f.text}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── 2) Cost & Efficiency ──────────────────────────────────
function CostEfficiencySection({ rows = [] }) {
  const TID_BY_METRIC = {
    'Food Cost %': 'food-cost-pct',
    'Beverage Cost %': 'beverage-cost-pct',
    'Electricity Cost': 'electricity-cost',
    'Gas Cost': 'gas-cost',
  };
  return (
    <Section title="Cost & Efficiency" icon={TrendingDown} testId="insights-cost-efficiency-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-[#1A2421]">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] border-b border-[#E2E8DF]">
              <th className="px-3 sm:px-4 py-3 font-medium">Metric</th>
              <th className="px-3 py-3 font-medium text-right">Actual</th>
              <th className="px-3 py-3 font-medium text-right">Budget</th>
              <th className="px-3 py-3 font-medium text-right">Variance</th>
              <th className="px-3 sm:px-4 py-3 font-medium text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isPct = r.unit === '%';
              const renderVal = (v) => v == null ? '—' : (isPct ? `${v}%` : `₹${fmt(v)}`);
              const valColor = r.status === 'over' ? 'text-[#C45A41]'
                : r.status === 'ok' ? 'text-[#2D6A4F]' : 'text-[#1A2421]';
              const statusInfo = r.status === 'over'
                ? { label: 'Over budget', cls: 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/30' }
                : r.status === 'ok'
                  ? { label: 'On track', cls: 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/30' }
                  : { label: 'No target', cls: 'bg-[#F3F5F2] text-[#5C6B64] border-[#E2E8DF]' };
              return (
                <tr key={r.metric} className="border-b border-[#E2E8DF] last:border-b-0 hover:bg-[#F9F9F8]/50 transition-colors"
                  data-testid={`insights-cost-row-${TID_BY_METRIC[r.metric] || r.metric.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 font-medium">{r.metric}</td>
                  <td className={`px-3 py-2.5 lg:py-3 text-right tabular-nums font-bold ${valColor}`}>
                    {renderVal(r.actual)}
                  </td>
                  <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums text-[#5C6B64]">{renderVal(r.budget)}</td>
                  <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums">
                    {r.variance != null ? (
                      <span className={`font-semibold ${r.variance > 0 ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                        {r.variance > 0 ? '+' : ''}{isPct ? `${r.variance}%` : `₹${fmt(r.variance)}`}
                      </span>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-right">
                    <Badge className={`text-[10px] font-medium ${statusInfo.cls}`}>{statusInfo.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ── 3) Customer Voice ─────────────────────────────────────
function CustomerVoiceSection({ voice }) {
  if (!voice) return null;
  const { outlet_ratings = [], touchpoints = {} } = voice;
  const tpRows = [
    { key: 'food', label: 'Food', val: touchpoints.food },
    { key: 'service', label: 'Service', val: touchpoints.service },
    { key: 'ambience', label: 'Ambience', val: touchpoints.ambience },
  ];
  const action = (
    <Badge variant="outline" className="border-[#E2E8DF] text-[10px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">
      {touchpoints.responses || 0} responses
    </Badge>
  );
  return (
    <Section title="Customer Voice" icon={Star} testId="insights-customer-voice-card" action={action}>
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-[#E2E8DF]">
        {/* Per-outlet rating summary */}
        <div className="overflow-x-auto">
          <div className="px-3 sm:px-4 pt-2 pb-2">
            <span className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">Ratings by Outlet</span>
          </div>
          <table className="w-full text-sm text-left text-[#1A2421]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] border-b border-[#E2E8DF]">
                <th className="px-3 sm:px-4 py-2.5 lg:py-3 font-medium">Outlet</th>
                <th className="px-3 py-2.5 lg:py-3 font-medium text-right">Avg</th>
                <th className="px-3 py-2.5 lg:py-3 font-medium text-right">Pos</th>
                <th className="px-3 py-2.5 lg:py-3 font-medium text-right">Neu</th>
                <th className="px-3 sm:px-4 py-2.5 lg:py-3 font-medium text-right">Neg</th>
              </tr>
            </thead>
            <tbody>
              {outlet_ratings.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-xs text-[#5C6B64]">No feedback in this range.</td></tr>
              )}
              {outlet_ratings.map((o) => (
                <tr key={o.outlet_id} className="border-b border-[#E2E8DF] last:border-b-0"
                  data-testid={`insights-rating-row-${o.outlet_id}`}>
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 font-medium">{o.outlet_name}</td>
                  <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums">
                    {o.avg_rating != null ? (
                      <span className={`font-bold ${o.avg_rating < 3.5 ? 'text-[#C45A41]' : o.avg_rating < 4 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}`}>
                        {o.avg_rating.toFixed(2)} ★
                      </span>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums text-[#2D6A4F] font-medium">{o.positive}</td>
                  <td className="px-3 py-2.5 lg:py-3 text-right tabular-nums text-[#5C6B64]">{o.neutral}</td>
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-right tabular-nums text-[#C45A41] font-medium">{o.negative}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Touchpoint averages */}
        <div className="px-3 sm:px-4 pt-2 pb-4 lg:pt-4">
          <span className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">Touchpoint Ratings</span>
          <div className="mt-3 space-y-3">
            {tpRows.map((r) => (
              <div key={r.key} className="flex items-center gap-3" data-testid={`insights-touchpoint-${r.key}`}>
                <span className="w-20 text-sm text-[#1A2421] font-medium">{r.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-[#F3F5F2] overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: r.val != null ? `${(r.val / 5) * 100}%` : '0%',
                      background: r.val != null && r.val >= 4 ? '#2D6A4F' : r.val != null && r.val >= 3.5 ? '#D4A017' : '#C45A41',
                    }}
                  />
                </div>
                <span className={`w-16 text-right tabular-nums font-semibold ${r.val != null && r.val < 3.5 ? 'text-[#C45A41]' : r.val != null && r.val < 4 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}`}>
                  {r.val != null ? `${r.val.toFixed(2)} ★` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ── 4) Feedback Details ──────────────────────────────────
function FeedbackDetailsSection({ items = [] }) {
  const [open, setOpen] = useState(false);
  const action = (
    <Badge variant="outline" className="border-[#E2E8DF] text-[10px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">
      {items.length} comments · 7 days
    </Badge>
  );
  return (
    <Section title="Feedback Details" icon={MessageCircle} testId="insights-feedback-card" action={action}>
      {items.length === 0 ? (
        <p className="px-3 sm:px-4 py-6 text-center text-sm text-[#5C6B64]">No customer comments in the last 7 days.</p>
      ) : (
        <>
          <div className="divide-y divide-[#E2E8DF]">
            {items.slice(0, 4).map((f, i) => (
              <FeedbackRow key={i} f={f} />
            ))}
          </div>
          {items.length > 4 && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full px-3 sm:px-4 py-3 text-xs font-semibold text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/5 border-t border-[#E2E8DF] flex items-center justify-center gap-1 transition-colors"
              data-testid="insights-feedback-expand-btn">
              See all {items.length} comments <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="insights-feedback-dialog">
          <DialogHeader>
            <DialogTitle>Customer Feedback — Last 7 days</DialogTitle>
            <DialogDescription>Recent comments captured across the selected outlets.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-[#E2E8DF] -mx-6">
            {items.map((f, i) => <FeedbackRow key={i} f={f} />)}
          </div>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function FeedbackRow({ f }) {
  const sentColor = f.sentiment === 'positive' ? 'text-[#2D6A4F]'
    : f.sentiment === 'negative' ? 'text-[#C45A41]'
      : 'text-[#5C6B64]';
  const sentBg = f.sentiment === 'positive' ? 'bg-[#2D6A4F]/10'
    : f.sentiment === 'negative' ? 'bg-[#C45A41]/10'
      : 'bg-[#F3F5F2]';
  return (
    <div className="px-3 sm:px-4 py-4" data-testid="insights-feedback-row">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-[0.06em] font-semibold px-2 py-0.5 rounded-full ${sentBg} ${sentColor}`}>
          {f.sentiment || 'neutral'}
        </span>
        <span className="text-xs text-[#5C6B64] font-medium">{f.outlet_name}</span>
        <span className="text-xs text-[#9CA3AF]">·</span>
        <span className="text-xs text-[#5C6B64]">{f.date}</span>
        {f.rating != null && <span className="text-xs text-[#1A2421] font-bold ml-auto tabular-nums">{Number(f.rating).toFixed(1)} ★</span>}
      </div>
      <p className="text-sm text-[#1A2421] leading-relaxed">{f.comment}</p>
      {f.name && <p className="text-[11px] text-[#5C6B64] mt-1.5">— {f.name}</p>}
    </div>
  );
}

// ── 5) Sentiment Summary ─────────────────────────────────
function SentimentSummarySection({ data }) {
  if (!data) return null;
  const { positive_pct = 0, neutral_pct = 0, negative_pct = 0, total = 0 } = data;
  if (total === 0) return null;
  return (
    <Section title="Sentiment" icon={CheckCircle2} testId="insights-sentiment-card"
      action={<Badge variant="outline" className="border-[#E2E8DF] text-[10px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">{total} responses</Badge>}>
      <div className="px-3 sm:px-4 pb-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <SentimentTile label="Positive" pct={positive_pct} color="text-[#2D6A4F]" testId="sentiment-positive" />
          <SentimentTile label="Neutral"  pct={neutral_pct}  color="text-[#5C6B64]" testId="sentiment-neutral" />
          <SentimentTile label="Negative" pct={negative_pct} color="text-[#C45A41]" testId="sentiment-negative" />
        </div>
        <div className="h-2 rounded-full bg-[#F3F5F2] overflow-hidden flex">
          <div className="h-full bg-[#2D6A4F] transition-all" style={{ width: `${positive_pct}%` }} />
          <div className="h-full bg-[#9CA3AF] transition-all" style={{ width: `${neutral_pct}%` }} />
          <div className="h-full bg-[#C45A41] transition-all" style={{ width: `${negative_pct}%` }} />
        </div>
      </div>
    </Section>
  );
}

function SentimentTile({ label, pct: pctVal, color, testId }) {
  return (
    <div className="border border-[#E2E8DF] rounded-lg p-3 sm:p-4 text-center" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] font-medium">{label}</p>
      <p className={`text-2xl sm:text-3xl font-bold mt-1 tabular-nums ${color}`}>{pctVal}%</p>
    </div>
  );
}

// ── 6) Checklist Failures ─────────────────────────────────
function ChecklistFailuresSection({ items = [] }) {
  const action = (
    <Badge variant="outline" className="border-[#E2E8DF] text-[10px] uppercase tracking-[0.06em] text-[#5C6B64] font-medium">
      {items.length} {items.length === 1 ? 'item' : 'items'}
    </Badge>
  );
  return (
    <Section title="Checklist Failures" icon={ClipboardX} testId="insights-checklist-failures-card" action={action}>
      {items.length === 0 ? (
        <div className="px-3 sm:px-4 py-7 text-center" data-testid="insights-checklist-empty">
          <CheckCircle2 className="w-8 h-8 text-[#2D6A4F] mx-auto mb-2" />
          <p className="text-sm text-[#1A2421] font-medium">All clear</p>
          <p className="text-xs text-[#5C6B64] mt-0.5">No failed checklist items in the selected range.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-[#1A2421]">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.06em] text-[#5C6B64] border-b border-[#E2E8DF]">
                <th className="px-3 sm:px-4 py-3 font-medium">Date</th>
                <th className="px-3 py-3 font-medium">Outlet</th>
                <th className="px-3 py-3 font-medium">Department</th>
                <th className="px-3 py-3 font-medium">Item</th>
                <th className="px-3 sm:px-4 py-3 font-medium">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-[#E2E8DF] last:border-b-0 hover:bg-[#F9F9F8]/50 transition-colors"
                  data-testid="insights-checklist-row">
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-[#5C6B64] whitespace-nowrap tabular-nums">{it.date}</td>
                  <td className="px-3 py-2.5 lg:py-3 font-medium">{it.outlet_name}</td>
                  <td className="px-3 py-2.5 lg:py-3 text-[#5C6B64]">{it.department || '—'}</td>
                  <td className="px-3 py-2.5 lg:py-3">{it.task_name}</td>
                  <td className="px-3 sm:px-4 py-2.5 lg:py-3 text-[#5C6B64] text-xs">{it.remarks || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ── Empty state ──────────────────────────────────────────
function EmptyState() {
  return (
    <Card className="border-[#E2E8DF] shadow-sm rounded-xl" data-testid="insights-empty-state">
      <CardContent className="px-6 py-16 sm:py-20 text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-[#F3F5F2] flex items-center justify-center mb-3">
          <Inbox className="w-5 h-5 text-[#5C6B64]" strokeWidth={1.75} />
        </div>
        <p className="text-base sm:text-lg font-semibold text-[#1A2421] tracking-tight">No data available for selected date</p>
        <p className="text-xs sm:text-sm text-[#5C6B64] mt-1.5 max-w-sm">Try a different date range or outlet selection to see insights.</p>
      </CardContent>
    </Card>
  );
}

// Page-level filter persistence: keep user's outlet/portfolio/range across
// navigation. Stored under a single key so the user's selection survives
// switching tabs, refresh, or coming back later.
const FILTER_STORAGE_KEY = 'restropulse:insights:filters:v1';
const loadFilters = () => {
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// ── Page ─────────────────────────────────────────────────
export default function AdminInsights() {
  const { getHeaders } = useAuth();
  const initial = loadFilters() || {};
  const [rangeKey, setRangeKey] = useState(initial.rangeKey || 'mtd');
  // Live (applied) scope — what's actually shown.
  const [portfolioId, setPortfolioId] = useState(initial.portfolioId || '');
  const [outletIds, setOutletIds] = useState(initial.outletIds || []);
  // ITER-Sales-Race generalisation — stage outlet/portfolio picks in
  // a pending draft and commit on explicit Apply. Date range + range
  // key still auto-fetch (no per-tick storm there).
  const [pendingPortfolioId, setPendingPortfolioId] = useState(initial.portfolioId || '');
  const [pendingOutletIds, setPendingOutletIds] = useState(initial.outletIds || []);
  const scopeDirty = (
    pendingPortfolioId !== portfolioId ||
    pendingOutletIds.length !== outletIds.length ||
    pendingOutletIds.some(id => !outletIds.includes(id))
  );
  const applyScope = useCallback(() => {
    setPortfolioId(pendingPortfolioId);
    setOutletIds(pendingOutletIds);
  }, [pendingPortfolioId, pendingOutletIds]);
  const resetScope = useCallback(() => {
    setPendingPortfolioId(portfolioId);
    setPendingOutletIds(outletIds);
  }, [portfolioId, outletIds]);
  // Custom date range — only used when rangeKey === 'custom'.
  const todayIso = new Date().toISOString().slice(0, 10);
  const monthStartIso = todayIso.slice(0, 8) + '01';
  const [customStart, setCustomStart] = useState(initial.customStart || monthStartIso);
  const [customEnd, setCustomEnd] = useState(initial.customEnd || todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Persist filters whenever any change.
  useEffect(() => {
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        rangeKey, portfolioId, outletIds, customStart, customEnd,
      }));
    } catch { /* localStorage unavailable — non-fatal */ }
  }, [rangeKey, portfolioId, outletIds, customStart, customEnd]);

  const range = useMemo(() => {
    if (rangeKey === 'custom') return { start: customStart, end: customEnd };
    return (QUICK_RANGES.find(r => r.key === rangeKey) || QUICK_RANGES[2]).getValue();
  }, [rangeKey, customStart, customEnd]);

  const scopeParam = useMemo(() => {
    const parts = [];
    if (portfolioId) parts.push(`portfolio_id=${portfolioId}`);
    if (outletIds && outletIds.length) parts.push(`outlet_ids=${outletIds.join(',')}`);
    return parts.length ? `&${parts.join('&')}` : '';
  }, [portfolioId, outletIds]);

  // Stale-response race guard — abort previous in-flight call, seq-id
  // gate before applying any state. Mirrors the SalesAnalyticsView fix.
  const reqSeqRef = useRef(0);
  const abortRef = useRef(null);
  // Track the first OutletFilter onChange (hydration from localStorage)
  // so we don't surface a transient 'dirty' Apply pill on mount.
  const hydratedRef = useRef(false);

  const load = useCallback(async () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const mySeq = ++reqSeqRef.current;
    const isLatest = () => mySeq === reqSeqRef.current && !controller.signal.aborted;

    setLoading(true);
    try {
      const url = `${API}/insights/five-minute?start_date=${range.start}&end_date=${range.end}${scopeParam}`;
      const res = await axios.get(url, { headers: getHeaders(), signal: controller.signal });
      if (!isLatest()) return;
      setData(res.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED'
          || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      console.error('Failed to load 5-Minute Insights', err);
      if (isLatest()) setData(null);
    } finally {
      if (mySeq === reqSeqRef.current) setLoading(false);
    }
  }, [range.start, range.end, scopeParam, getHeaders]);

  useEffect(() => { load(); }, [load]);

  // Truly empty for the selected range: no in-range sales/feedback/failures.
  // (`feedback_recent` is a global last-7-days window — intentionally excluded.)
  const hasAnyData = !!data && (
    (data.sales_channel?.total_sales || 0) > 0 ||
    (data.sentiment?.total || 0) > 0 ||
    (data.checklist_failures?.length || 0) > 0
  );

  return (
    <div className="w-full" data-testid="five-minute-insights-view">
        {/* Compact header — single row, title + controls */}
        <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-lg sm:text-xl font-semibold text-[#1A2421] tracking-tight">5-Minute Insights</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <OutletFilter
              portfolioId={pendingPortfolioId}
              outletIds={pendingOutletIds}
              onChange={({ portfolioId: pid, outletIds: oids }) => {
                // OutletFilter hydrates from localStorage on mount, which
                // fires onChange once before any user interaction. Sync
                // BOTH live and pending state on that first hydration so
                // the Apply pill doesn't show a transient 'dirty' state.
                if (!hydratedRef.current) {
                  hydratedRef.current = true;
                  setPortfolioId(pid || '');
                  setOutletIds(oids || []);
                }
                setPendingPortfolioId(pid || '');
                setPendingOutletIds(oids || []);
              }}
            />
            <Button
              size="sm"
              disabled={!scopeDirty}
              onClick={applyScope}
              data-testid="insights-apply-scope"
              className={`h-8 text-xs ${scopeDirty
                ? 'bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
              Apply
            </Button>
            {scopeDirty && (
              <button type="button"
                onClick={resetScope}
                data-testid="insights-reset-scope"
                className="text-[10px] text-slate-500 hover:text-slate-700 underline underline-offset-2">
                reset
              </button>
            )}
            <Select value={rangeKey} onValueChange={setRangeKey}>
              <SelectTrigger className="h-8 text-xs border-[#E2E8DF] w-[140px]" data-testid="insights-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUICK_RANGES.map(r => (
                  <SelectItem key={r.key} value={r.key} data-testid={`insights-range-option-${r.key}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rangeKey === 'custom' && (
              <div className="flex items-center gap-1.5" data-testid="insights-custom-range">
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={e => setCustomStart(e.target.value)}
                  className="h-8 text-xs px-2 border border-[#E2E8DF] rounded text-[#1A2421]"
                  data-testid="insights-custom-start"
                  aria-label="Custom range start date"
                />
                <span className="text-[11px] text-[#5C6B64]">→</span>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="h-8 text-xs px-2 border border-[#E2E8DF] rounded text-[#1A2421]"
                  data-testid="insights-custom-end"
                  aria-label="Custom range end date"
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
          </div>
        ) : !hasAnyData ? (
          <EmptyState />
        ) : (
          <>
            <KpiStrip data={data} />
            <SalesChannelSection data={data?.sales_channel} />
            <CostEfficiencySection rows={data?.cost_efficiency || []} />
            <CustomerVoiceSection voice={data?.customer_voice} />
            <FeedbackDetailsSection items={data?.feedback_recent || []} />
            <SentimentSummarySection data={data?.sentiment} />
            <ChecklistFailuresSection items={data?.checklist_failures || []} />
          </>
        )}
      </div>
  );
}
